// The increase threshold editor (spec C14 Defaults; design/kit SettingsEditor; Yehuda, Oct 29): the
// percent and the minimum dollars. The Manager and the Admin open it (Rachy owns the threshold).
import { useState } from 'react';
import type { Api, IncreaseRule } from '../auth/api';
import {
  draftOf,
  dollarsOf,
  increaseCopy as c,
  percentOf,
  ruleOf,
  saveError,
  type IncreaseDraft,
} from './increase-rule-lines';
import { ErrorLine, SettingsEditor } from './SettingsEditor';

type Props = {
  api: Api;
  rule: IncreaseRule;
  onClose: () => void;
  onSaved: (toast: string) => void;
};

export function IncreaseRuleEditor({ api, rule, onClose, onSaved }: Props) {
  const [orig] = useState(() => draftOf(rule));
  const [d, setD] = useState<IncreaseDraft>(orig);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = d.percent !== orig.percent || d.dollars !== orig.dollars;
  const next = ruleOf(d);
  const percentBad = d.percent !== orig.percent && percentOf(d.percent) === null;
  const dollarsBad = d.dollars !== orig.dollars && dollarsOf(d.dollars) === null;

  async function save() {
    if (!next) return;
    setBusy(true);
    setError(null);
    const res = await api.saveIncreaseRule(next);
    setBusy(false);
    if (res.ok) onSaved(c.saved);
    else setError(saveError(res.error));
  }

  return (
    <SettingsEditor
      id="ir"
      title={c.title}
      sub={c.sub}
      dirty={dirty}
      valid={!!next}
      busy={busy}
      error={error}
      onSave={() => void save()}
      onClose={onClose}
    >
      <div>
        <label className="flab" htmlFor="ir-percent">
          {c.percent}
        </label>
        <div className="col g6">
          <input
            id="ir-percent"
            className="input mono"
            inputMode="decimal"
            autoComplete="off"
            value={d.percent}
            aria-invalid={percentBad || undefined}
            aria-describedby={percentBad ? 'ir-percent-err' : undefined}
            onChange={(e) => setD({ ...d, percent: e.target.value })}
          />
          {percentBad && <ErrorLine id="ir-percent-err" text={c.percentError} />}
        </div>
      </div>
      <div>
        <label className="flab" htmlFor="ir-dollars">
          {c.dollars}
        </label>
        <div className="col g6">
          <input
            id="ir-dollars"
            className="input mono"
            inputMode="numeric"
            autoComplete="off"
            value={d.dollars}
            aria-invalid={dollarsBad || undefined}
            aria-describedby={dollarsBad ? 'ir-dollars-err' : 'ir-dollars-hint'}
            onChange={(e) => setD({ ...d, dollars: e.target.value })}
          />
          {dollarsBad ? (
            <ErrorLine id="ir-dollars-err" text={c.dollarsError} />
          ) : (
            <p id="ir-dollars-hint" className="fnote m0">
              {c.dollarsHint}
            </p>
          )}
        </div>
      </div>
    </SettingsEditor>
  );
}
